/**
 * Per-language slug map for disease articles.
 *
 * The canonical (Estonian) `id` matches the `slug` field in
 * data/diseaseArticles.js. For RU we use pure Cyrillic slugs (per user
 * preference — 2026-07); for EN we use readable English slugs.
 *
 * URLs render as:
 *   ET:  /haigused/1-tuupi-diabeet
 *   RU:  /ru/заболевания/сахарный-диабет-1-типа
 *   EN:  /en/diseases/type-1-diabetes
 *
 * Legacy fallback: If a user hits the Estonian slug inside a /ru or /en URL
 * (e.g. an old Google-indexed link), DiseaseArticle.jsx redirects to the
 * canonical localised slug — no article lookup ever fails.
 */

export const DISEASE_SLUGS = [
  {
    id: "1-tuupi-diabeet",
    slugs: {
      et: "1-tuupi-diabeet",
      ru: "сахарный-диабет-1-типа",
      en: "type-1-diabetes",
    },
  },
  {
    id: "2-tuupi-diabeet",
    slugs: {
      et: "2-tuupi-diabeet",
      ru: "сахарный-диабет-2-типа",
      en: "type-2-diabetes",
    },
  },
  {
    id: "diabeetiline-retinopaatia",
    slugs: {
      et: "diabeetiline-retinopaatia",
      ru: "диабетическая-ретинопатия",
      en: "diabetic-retinopathy",
    },
  },
  {
    id: "diabeetiline-neuropaatia",
    slugs: {
      et: "diabeetiline-neuropaatia",
      ru: "диабетическая-нейропатия",
      en: "diabetic-neuropathy",
    },
  },
  {
    id: "diabeetiline-nefropaatia",
    slugs: {
      et: "diabeetiline-nefropaatia",
      ru: "диабетическая-нефропатия",
      en: "diabetic-nephropathy",
    },
  },
  {
    id: "diabeedi-uuringud",
    slugs: {
      et: "diabeedi-uuringud",
      ru: "анализы-при-диабете",
      en: "diabetes-lab-tests",
    },
  },
  {
    id: "diabeedi-ravimid",
    slugs: {
      et: "diabeedi-ravimid",
      ru: "лекарства-от-диабета",
      en: "diabetes-medications",
    },
  },
  {
    id: "soomine-liikumine-kehakaalu-langetamine",
    slugs: {
      et: "soomine-liikumine-kehakaalu-langetamine",
      ru: "питание-движение-снижение-веса",
      en: "diet-exercise-weight-loss",
    },
  },
  {
    id: "kondimine-peale-sooki",
    slugs: {
      et: "kondimine-peale-sooki",
      ru: "ходьба-после-еды",
      en: "walking-after-meals",
    },
  },
];

// Fast lookups
const BY_ID = Object.fromEntries(DISEASE_SLUGS.map((d) => [d.id, d.slugs]));
const BY_ANY_SLUG = {};
for (const d of DISEASE_SLUGS) {
  for (const s of Object.values(d.slugs)) BY_ANY_SLUG[s] = d.id;
}

/** Return the id (canonical ET slug) for any known localised slug. */
export function articleIdFromSlug(slug) {
  return BY_ANY_SLUG[slug] || null;
}

/** Return the localised slug for an article id in the given language. */
export function articleSlug(id, lang) {
  return BY_ID[id]?.[lang] || id;
}
